import { For, Show, createEffect, createSignal } from "solid-js";

import { useAgents, useAiModelCatalog, useAiSettings, useUpdateAgent } from "~/lib/hooks";
import type { AgentRead, AgentUpdate } from "~/lib/types";

type Props = {
  wsKey: () => string;
};

type EditState = AgentRead;

type FormState = {
  name: string;
  description: string;
  enabled: boolean;
  preferred_model_id: string;
  system_prompt_override: string;
};

/**
 * Admin panel listing the workspace's agents (auto-registered on first
 * use) with toggle + per-agent model + system-prompt override.
 *
 * The model picker is filtered to the workspace's effective allowlist
 * (intersection of partner + workspace AI settings) — agents can't
 * preferred a model that policy excludes.
 */
export function AgentsPanel(props: Props) {
  const agents = useAgents(props.wsKey);
  const aiSettings = useAiSettings(props.wsKey);
  const catalog = useAiModelCatalog(props.wsKey, () => aiSettings.data != null);
  const update = useUpdateAgent(props.wsKey);

  const [edit, setEdit] = createSignal<EditState | null>(null);
  const [form, setForm] = createSignal<FormState>({
    name: "",
    description: "",
    enabled: true,
    preferred_model_id: "",
    system_prompt_override: "",
  });
  const [error, setError] = createSignal<string | null>(null);

  // Re-hydrate the form when the edit target changes.
  createEffect(() => {
    const a = edit();
    if (!a) return;
    setError(null);
    setForm({
      name: a.name,
      description: a.description ?? "",
      enabled: a.enabled,
      preferred_model_id: a.preferred_model_id ?? "",
      system_prompt_override: a.system_prompt_override ?? "",
    });
  });

  // Models the picker offers: only those in the effective allowlist.
  // If no policy is set (allowed_model_ids === null), show everything
  // from the catalog.
  const allowedModels = () => {
    const allowed = aiSettings.data?.effective.allowed_model_ids;
    const all = catalog.data?.models ?? [];
    if (allowed === null || allowed === undefined) return all;
    const set = new Set(allowed);
    return all.filter((m) => set.has(m.id));
  };

  // Inline toggle without opening the modal — the common admin action
  // is "pause this agent", not "rewrite its prompt".
  const toggleEnabled = async (a: AgentRead) => {
    try {
      await update.mutateAsync({
        agentId: a.id,
        version: a.version,
        body: { enabled: !a.enabled },
      });
    } catch (err) {
      window.alert((err as Error).message);
    }
  };

  const handleSave = async (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    const target = edit();
    if (!target) return;
    const f = form();
    const body: AgentUpdate = {
      name: f.name.trim() || target.name,
      description: f.description.trim() || null,
      enabled: f.enabled,
      preferred_model_id: f.preferred_model_id.trim() || null,
      system_prompt_override: f.system_prompt_override.trim() || null,
    };
    try {
      await update.mutateAsync({
        agentId: target.id,
        version: target.version,
        body,
      });
      setEdit(null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div>
          <h3 class="card-title text-base">✨ Agents</h3>
          <p class="text-xs opacity-60 mt-0.5">
            Built-in agents are auto-registered on first use. Toggle to pause, edit to override the
            default model or system prompt.
          </p>
        </div>

        <Show when={agents.isLoading}>
          <p class="text-sm opacity-60 mt-2">Loading…</p>
        </Show>

        <Show when={!agents.isLoading && (agents.data?.items.length ?? 0) === 0}>
          <p class="text-sm opacity-60 mt-2">
            No agents yet. Trigger one (e.g. "✨ Generate brief" on an account) and it'll register
            itself here.
          </p>
        </Show>

        <div class="overflow-x-auto mt-2">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Model override</th>
                <th>Enabled</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <For each={agents.data?.items ?? []}>
                {(a) => (
                  <tr>
                    <td>
                      <div class="font-medium">{a.name}</div>
                      <Show when={a.description}>
                        <div class="text-xs opacity-60 max-w-md line-clamp-2">{a.description}</div>
                      </Show>
                    </td>
                    <td class="text-xs opacity-60">
                      <code>{a.key}</code>
                    </td>
                    <td class="text-xs">
                      <Show
                        when={a.preferred_model_id}
                        fallback={<span class="opacity-50">— uses default</span>}
                      >
                        <code>{a.preferred_model_id}</code>
                      </Show>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        class="toggle toggle-sm toggle-primary"
                        checked={a.enabled}
                        aria-label={`Toggle ${a.name}`}
                        onChange={() => toggleEnabled(a)}
                      />
                    </td>
                    <td class="text-right whitespace-nowrap">
                      <button type="button" class="btn btn-ghost btn-xs" onClick={() => setEdit(a)}>
                        Edit…
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>

        <Show when={edit()}>
          {(target) => (
            <dialog
              open
              class="modal modal-open"
              aria-modal="true"
              aria-labelledby="agent-edit-title"
            >
              <form onSubmit={handleSave} class="modal-box max-w-xl">
                <h3 id="agent-edit-title" class="font-bold text-lg mb-3">
                  Edit {target().name}
                </h3>

                <div class="flex flex-col gap-3">
                  <div>
                    <label class="label">
                      <span class="label-text">Name</span>
                    </label>
                    <input
                      type="text"
                      class="input input-bordered w-full"
                      value={form().name}
                      onInput={(e) => setForm((f) => ({ ...f, name: e.currentTarget.value }))}
                      required
                    />
                  </div>

                  <div>
                    <label class="label">
                      <span class="label-text">Description</span>
                    </label>
                    <textarea
                      class="textarea textarea-bordered w-full"
                      rows={2}
                      value={form().description}
                      onInput={(e) =>
                        setForm((f) => ({
                          ...f,
                          description: e.currentTarget.value,
                        }))
                      }
                    />
                  </div>

                  <div class="form-control">
                    <label class="label cursor-pointer justify-start gap-2">
                      <input
                        type="checkbox"
                        class="checkbox checkbox-sm"
                        checked={form().enabled}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            enabled: e.currentTarget.checked,
                          }))
                        }
                      />
                      <span class="label-text">Enabled</span>
                    </label>
                  </div>

                  <div>
                    <label class="label">
                      <span class="label-text">Preferred model (optional)</span>
                    </label>
                    <select
                      class="select select-bordered w-full"
                      value={form().preferred_model_id}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          preferred_model_id: e.currentTarget.value,
                        }))
                      }
                    >
                      <option value="">— workspace default —</option>
                      <For each={allowedModels()}>
                        {(m) => <option value={m.id}>{m.label}</option>}
                      </For>
                    </select>
                    <p class="text-xs opacity-60 mt-1">
                      Picker is filtered to your workspace's effective allowlist.
                    </p>
                  </div>

                  <div>
                    <label class="label">
                      <span class="label-text">System prompt override</span>
                    </label>
                    <textarea
                      class="textarea textarea-bordered w-full font-mono text-xs"
                      rows={6}
                      placeholder="Leave blank to use the builtin default."
                      value={form().system_prompt_override}
                      onInput={(e) =>
                        setForm((f) => ({
                          ...f,
                          system_prompt_override: e.currentTarget.value,
                        }))
                      }
                    />
                  </div>

                  <Show when={error()}>
                    <div role="alert" class="alert alert-error text-sm">
                      {error()}
                    </div>
                  </Show>
                </div>

                <div class="modal-action">
                  <button type="button" class="btn btn-ghost" onClick={() => setEdit(null)}>
                    Cancel
                  </button>
                  <button type="submit" class="btn btn-primary" disabled={update.isPending}>
                    {update.isPending ? "Saving…" : "Save"}
                  </button>
                </div>
              </form>
            </dialog>
          )}
        </Show>
      </div>
    </div>
  );
}
